
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem("apex_user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  // Refresh user from /me on mount when token exists
  useEffect(() => {
    const token = localStorage.getItem("apex_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((res) => {
        setUser(res.data);
        localStorage.setItem("apex_user", JSON.stringify(res.data));
      })
      .catch(() => {
        localStorage.removeItem("apex_token");
        localStorage.removeItem("apex_user");
        setUser(null);
      })
      .finally(() => setLoading(false));

    const onLogout = () => setUser(null);
    window.addEventListener("apex:logout", onLogout);
    return () => window.removeEventListener("apex:logout", onLogout);
  }, []);

  // Refresh user from /auth/me — used after self-service profile edits so
  // the rest of the app immediately reflects the new firstName/phone/etc.,
  // and also right after login/register (see below) since those endpoints
  // intentionally return only a minimal user object for a fast response.
  const refreshUser = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem("apex_user", JSON.stringify(data));
      return data;
    } catch {
      return null;
    }
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("apex_token", data.token);
    localStorage.setItem("apex_user", JSON.stringify(data.user));
    setUser(data.user);
    // /auth/login only returns a minimal user object (id/email/firstName/
    // lastName/role). Hydrate the FULL profile right away (packageOfferAt,
    // packageOfferSeen, phone, vehicles, promoCodes, ...) so features like
    // the NotificationBell package-offer nudge work immediately post-login
    // instead of only after a page reload.
    const full = await refreshUser();
    return full || data.user;
  }, [refreshUser]);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("apex_token", data.token);
    localStorage.setItem("apex_user", JSON.stringify(data.user));
    setUser(data.user);
    const full = await refreshUser();
    return full || data.user;
  }, [refreshUser]);

  const logout = useCallback(() => {
    localStorage.removeItem("apex_token");
    localStorage.removeItem("apex_user");
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, setUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
